import React, { useState, useEffect } from 'react';
import {
  Button, Form, Container, Row, Col, Dropdown, ButtonGroup
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import useImageApi from '../../managers/ImageManager';
import useProductApi from '../../managers/ProductManager';
import Loading, { LoadingStates } from '../../components/Loading';
import { useAuth0 } from '@auth0/auth0-react';
import { FaPalette, FaVectorSquare, FaTree } from 'react-icons/fa6';
import { Helmet } from 'react-helmet-async';
import useCatalogApi, { CatalogItem } from '../../managers/CatalogManager';

// Creative loading messages for AI generation
const GENERATION_MESSAGES = [
  "Remixing your masterpiece...",
  "AI is reimagining your design...",
  "Transforming your vision...",
  "Blending creativity with innovation...",
  "Your remix is taking shape...",
  "Watch your ideas evolve...",
  "Creating something new from your original...",
  "Mixing art with imagination...",
  "Your new variation is emerging...",
  "Almost there... finalizing your remix..."
];

const Remix: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    remixSourceImageUrl?: string;
    remixSourceImageId?: string;
    productPreviewUrl?: string;
    catalogItemId?: string;
    catalogItemVariantId?: string;
    originalPrompt?: string;
    originalProductName?: string;
    originalTags?: string[];
  } | null;

  const [loadingState, setLoadingState] = useState(LoadingStates.SUCCESS);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [messageIndex, setMessageIndex] = useState(0);

  const [remixPrompt, setRemixPrompt] = useState('');
  const [remixMode, setRemixMode] = useState<'fine_tune' | 'retheme' | 'redesign'>('fine_tune');
  const [showFullOriginalPrompt, setShowFullOriginalPrompt] = useState(false);

  const [imageModel, setImageModel] = useState('gpt-image-2');
  const [imageModelDropdownOpen, setImageModelDropdownOpen] = useState(false);
  const toggleImageModelDropdown = () => setImageModelDropdownOpen((prev) => !prev);

  const [imageStyle, setImageStyle] = useState('vivid');
  const [styleDropdownOpen, setStyleDropdownOpen] = useState(false);
  const toggleStyleDropdown = () => setStyleDropdownOpen((prev) => !prev);

  const { getCatalogItems } = useCatalogApi();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);

  const imageModelIcons: Record<string, JSX.Element> = {
    "gpt-image-2": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        gpt-image-2
      </span>
    ),
    "gpt-image-1": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        gpt-image-1
      </span>
    ),
    "gpt-image-1.5": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        gpt-image-1.5
      </span>
    ),
    "dall-e-3": (
      <span className="d-flex align-items-center gap-2">
        <FaVectorSquare />
        dall-e-3
      </span>
    ),
  };

  const styleIcons: Record<string, JSX.Element> = {
    vivid: (
      <span className="d-flex align-items-center gap-2">
        <FaPalette />
        Vivid
      </span>
    ),
    natural: (
      <span className="d-flex align-items-center gap-2">
        <FaTree />
        Natural
      </span>
    ),
  };

  const { generateProduct } = useProductApi();
  const { generateImageAsync, getImageBaseUrl } = useImageApi();
  const { user } = useAuth0();

  // Validate we have the required data
  useEffect(() => {
    console.log('[Remix] Received state:', {
      hasRemixSourceImageUrl: !!state?.remixSourceImageUrl,
      hasProductPreviewUrl: !!state?.productPreviewUrl,
      hasCatalogItemId: !!state?.catalogItemId,
      hasCatalogItemVariantId: !!state?.catalogItemVariantId,
      hasOriginalPrompt: !!state?.originalPrompt,
      hasOriginalProductName: !!state?.originalProductName,
      hasOriginalTags: !!state?.originalTags,
      remixSourceImageUrl: state?.remixSourceImageUrl,
      productPreviewUrl: state?.productPreviewUrl,
      catalogItemId: state?.catalogItemId,
      catalogItemVariantId: state?.catalogItemVariantId,
      originalPrompt: state?.originalPrompt?.substring(0, 100) + '...',
      originalProductName: state?.originalProductName,
      originalTags: state?.originalTags,
    });

    if (!state?.remixSourceImageUrl || !state?.catalogItemId || !state?.catalogItemVariantId) {
      console.error('[Remix] Missing required data, redirecting to create');
      navigate('/products/create');
    }
  }, [state, navigate]);

  // Load catalog items
  useEffect(() => {
    getCatalogItems()
      .then((res) => {
        const items = Array.isArray(res) ? res : res.items;
        if (Array.isArray(items)) {
          setCatalogItems(items);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch catalog items:", err);
      });
  }, [getCatalogItems]);

  const selectedCatalogItem = catalogItems?.find(item => item.id === state?.catalogItemId);
  const catalogVariants = selectedCatalogItem?.variants || [];
  const selectedCatalogVariant = catalogVariants?.find(variant => variant.id === state?.catalogItemVariantId);

  // Cycle through generation messages every 15 seconds while loading
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (loadingState === LoadingStates.LOADING) {
      interval = setInterval(() => {
        setMessageIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % GENERATION_MESSAGES.length;
          setLoadingMessage(GENERATION_MESSAGES[newIndex]);
          return newIndex;
        });
      }, 15000); // 15 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loadingState]);

  const handleRemix = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!remixPrompt.trim()) {
      alert("Please enter modifications to your original prompt.");
      return;
    }

    if (!state?.remixSourceImageUrl || !state?.catalogItemId || !state?.catalogItemVariantId) {
      alert("Missing required remix data. Please try again.");
      return;
    }

    // Use remix prompt as standalone - let AI modify the image based solely on the new prompt
    const fullPrompt = remixPrompt;

    // Start with a random creative message
    const randomIndex = Math.floor(Math.random() * GENERATION_MESSAGES.length);
    setMessageIndex(randomIndex);
    setLoadingMessage(GENERATION_MESSAGES[randomIndex]);
    setLoadingState(LoadingStates.LOADING);

    try {
      console.log('[Remix] Starting remix with:', {
        originalPrompt: state.originalPrompt,
        remixPrompt,
        fullPrompt,
        sourceImageUrl: state.remixSourceImageUrl,
        catalogItemId: state.catalogItemId,
        catalogItemVariantId: state.catalogItemVariantId
      });

      // Generate new image using generate-from-source
      // The backend will intelligently parse constraints and build the remix prompt
      const image = await generateImageAsync({
        userId: user?.sub || "0",
        action: 'generate-from-source',
        prompt: fullPrompt,
        vendorId: 'openai',
        imageModel: imageModel || 'gpt-image-2',
        imageShape: selectedCatalogVariant?.orientation || selectedCatalogItem?.orientation || 'square',
        imageStyle,
        sourceImageUrl: state.remixSourceImageUrl, // Deprecated, kept for backwards compat
        sourceImageId: state.remixSourceImageId, // Preferred method
        imageName: state.originalProductName ? `${state.originalProductName} (Remix)` : 'Remixed Product',
        imageTags: state.originalTags ?? [],
        // Pass mode selection and original prompt - AI will parse constraints from natural language
        remixMode,
        originalPrompt: state.originalPrompt
      });

      console.log('[Remix] Image result:', image);

      if (!image?.imageUrl) {
        // If status is completed but imageUrl is missing, try to construct it
        if (image?.status === 'completed' && image?.imageKey) {
          const cleanImageKey = image.imageKey.startsWith('/')
            ? image.imageKey.substring(1)
            : image.imageKey;
          image.imageUrl = `${getImageBaseUrl()}/${cleanImageKey}`;
          console.log('[Remix] Constructed imageUrl from imageKey:', image.imageUrl);
        } else {
          console.error('[Remix] Image generation result missing imageUrl:', image);
          throw new Error(`Image generation failed. Status: ${image?.status || 'unknown'}, Error: ${image?.errorMessage || 'No error message'}`);
        }
      }

      console.log('[Remix] Image generated successfully:', {
        imageId: image.imageId,
        imageUrl: image.imageUrl,
        imageName: image.imageName
      });

      setLoadingMessage("Generating Product Preview...");

      // Small delay to help with eventual consistency issues
      await new Promise(resolve => setTimeout(resolve, 500));

      const product = await generateProduct({
        userId: user?.sub || "0",
        sourceImageUrl: image.imageUrl,
        sourceImageId: image.imageId,
        catalogItemId: state.catalogItemId,
        catalogItemVariantId: state.catalogItemVariantId
      });

      console.log('[Remix] Product generated successfully:', {
        productId: product.productId,
        previewImageUrls: product.previewImageUrls
      });

      if (!product?.previewImageUrls?.[0]) throw new Error('Product preview failed.');

      setLoadingState(LoadingStates.SUCCESS);

      // Navigate to product detail
      const productUrl = `/products/${state.catalogItemId}/${selectedCatalogItem?.category || 'uncategorized'}/${product.productId}`;
      navigate(productUrl);
    } catch (err: any) {
      console.error('[Remix] Error:', err);
      setLoadingMessage(err.message);
      setLoadingState(LoadingStates.ERROR);
    }
  };

  if (!state?.remixSourceImageUrl) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md="8" className="text-center">
            <h1>Invalid Remix Request</h1>
            <p>Missing source image data. Please try again.</p>
            <Button onClick={() => navigate('/products/create')}>Go to Create</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  const getTruncatedOriginalPrompt = () => {
    if (!state?.originalPrompt) return '';
    return state.originalPrompt.length > 250 && !showFullOriginalPrompt
      ? state.originalPrompt.slice(0, 250) + "..."
      : state.originalPrompt;
  };

  return (
    <>
      <Helmet>
        <title>Remix Your Product | Wallscapes.AI</title>
        <meta name="description" content="Remix and refine your AI-generated art. Make adjustments to colors, themes, and design elements." />
      </Helmet>
      {/* Overlay Loading Screen with Image Processing Effect */}
      {loadingState === LoadingStates.LOADING && (
        <Loading
          state={loadingState}
          message={loadingMessage}
          subtitle="Please don't close this window. We're remixing something amazing for you!"
          overlay={true}
          image={state.productPreviewUrl || state.remixSourceImageUrl}
        />
      )}

      <Container className="py-5">
        <Row className="align-items-center text-center justify-content-center mb-5">
          <Col md="10" lg="8">
            <h1 className="display-4">
              <FaPalette className="me-2" />
              Remix Your Product
            </h1>
            <p className="lead">
              Add your creative modifications to transform the original design into something new!
            </p>

            {/* Original Product Info */}
            <Row className="mb-5 my-5">
              <Col xs="12">
                <h3 className="mb-3">Original Design</h3>
                <div className="bg-light border rounded p-4">
                  <Row className="align-items-center">
                    <Col xs="12" md="5" className="mb-3 mb-md-0">
                      <img
                        src={state.productPreviewUrl || state.remixSourceImageUrl}
                        alt="Original product"
                        className="img-fluid rounded"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                      />
                    </Col>
                    <Col xs="12" md="7" className="text-start">
                      <h5 className="mb-3">Product Type</h5>
                      <p className="fw-bold">{selectedCatalogItem?.name || 'Loading...'}</p>
                      {selectedCatalogVariant?.name && (
                        <p className="text-muted">Size: {selectedCatalogVariant.name}</p>
                      )}

                      {state.originalPrompt && (
                        <>
                          <h5 className="mb-2 mt-3">Original Prompt</h5>
                          <div className="bg-white border rounded p-3">
                            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                              {getTruncatedOriginalPrompt()}
                              {state.originalPrompt.length > 250 && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  onClick={() => setShowFullOriginalPrompt(!showFullOriginalPrompt)}
                                >
                                  {showFullOriginalPrompt ? "Show Less" : "More..."}
                                </Button>
                              )}
                            </p>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            {/* Remix Input */}
            <Row className="mb-5">
              <Col>
                <h3 className="mb-3">Describe Your Changes</h3>
                <div className="bg-light border border-secondary rounded text-start p-3 mb-3">
                  <span className="badge bg-primary me-2">AI-Powered Remix</span>
                  Choose your remix style and describe what you want to change. Our AI will automatically understand what to keep and what to modify.
                </div>

                <Form onSubmit={handleRemix}>
                  {/* Remix Mode Selector */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Remix Style</Form.Label>
                    <ButtonGroup className="w-100 d-flex">
                      <Button
                        variant={remixMode === 'fine_tune' ? 'primary' : 'outline-primary'}
                        onClick={() => setRemixMode('fine_tune')}
                        className="flex-fill"
                      >
                        <div className="d-flex flex-column align-items-center py-2">
                          <strong>Fine Tune</strong>
                          <small className="text-muted">Subtle refinements</small>
                        </div>
                      </Button>
                      <Button
                        variant={remixMode === 'retheme' ? 'primary' : 'outline-primary'}
                        onClick={() => setRemixMode('retheme')}
                        className="flex-fill"
                      >
                        <div className="d-flex flex-column align-items-center py-2">
                          <strong>Retheme</strong>
                          <small className="text-muted">New style/colors</small>
                        </div>
                      </Button>
                      <Button
                        variant={remixMode === 'redesign' ? 'primary' : 'outline-primary'}
                        onClick={() => setRemixMode('redesign')}
                        className="flex-fill"
                      >
                        <div className="d-flex flex-column align-items-center py-2">
                          <strong>Redesign</strong>
                          <small className="text-muted">Creative freedom</small>
                        </div>
                      </Button>
                    </ButtonGroup>
                    <Form.Text className="text-muted">
                      {remixMode === 'fine_tune' && 'Make small adjustments while preserving the overall design'}
                      {remixMode === 'retheme' && 'Change the visual theme while keeping core elements'}
                      {remixMode === 'redesign' && 'Allow major creative changes with inspiration from original'}
                    </Form.Text>
                  </Form.Group>

                  {/* Single Remix Input */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">What would you like to change?</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Examples:&#10;• Make it more vibrant with golden accents&#10;• Change to a cyberpunk style but keep the main subject&#10;• Add sunset lighting in the background&#10;• Make it darker and more mysterious&#10;• Convert to watercolor painting style&#10;• Just brighten the colors slightly"
                      value={remixPrompt}
                      onChange={(e) => setRemixPrompt(e.target.value)}
                      className="mb-2"
                      style={{ fontSize: '1.05rem' }}
                    />
                    <Form.Text className="text-muted d-block mb-3">
                      💡 <strong>Pro tip:</strong> Be specific about what you want to change. You can mention what to keep (e.g., "but keep the composition") or what areas to modify (e.g., "only the background colors").
                    </Form.Text>
                  </Form.Group>

                  <div className="d-flex gap-2 mb-3">
                    {/* Image Model dropdown */}
                    <Dropdown show={imageModelDropdownOpen} onToggle={toggleImageModelDropdown}>
                      <Dropdown.Toggle className="btn btn-light">
                        {imageModelIcons[imageModel]}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Object.entries(imageModelIcons).map(([model, icon]) => (
                          <Dropdown.Item
                            key={model}
                            active={imageModel === model}
                            onClick={() => setImageModel(model)}
                          >
                            {icon}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Image Style dropdown */}
                    <Dropdown show={styleDropdownOpen} onToggle={toggleStyleDropdown}>
                      <Dropdown.Toggle className="btn btn-light">
                        {styleIcons[imageStyle]}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Object.entries(styleIcons).map(([style, icon]) => (
                          <Dropdown.Item
                            key={style}
                            active={imageStyle === style}
                            onClick={() => setImageStyle(style)}
                          >
                            {icon}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <Button
                    size="lg"
                    className="w-100"
                    variant="primary"
                    disabled={loadingState === LoadingStates.LOADING || !remixPrompt.trim()}
                    type="submit"
                  >
                    <FaPalette className="me-2" />
                    Remix Product
                  </Button>
                </Form>
              </Col>
            </Row>

            {(loadingState === LoadingStates.ERROR) && (
              <Loading state={loadingState} message={loadingMessage} />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Remix;
